import EquationInput from "../../../../components/inputs/EquationInput";
import { InlineMath, BlockMath } from "react-katex";
import { checkCircleEquation, parseCanonicalCircleEquation } from "../../../../utils/circleEquation";

export const id = "ConvertToCanonicalTask";
export const displayName = "Przekształć do postaci kanonicznej";

const EPSILON = 0.001;

function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac < EPSILON) return `${sign}${intPart}`;

  for (let denominator = 2; denominator <= 24; denominator += 1) {
    const numerator = Math.round(frac * denominator);
    if (Math.abs(numerator / denominator - frac) < EPSILON) {
      const divisor = gcd(numerator, denominator);
      const num = numerator / divisor;
      const den = denominator / divisor;
      if (intPart === 0) return `${sign}\\dfrac{${num}}{${den}}`;
      return `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }

  return `${sign}${abs.toFixed(2)}`;
}

function formatSignedTerm(variable, coefficient) {
  if (coefficient === 0) return "";
  const sign = coefficient > 0 ? "+" : "-";
  const absolute = Math.abs(coefficient);
  const coefficientLatex = absolute === 1 ? "" : formatMixedFractionLatex(absolute);
  return `${sign} ${coefficientLatex}${variable}`;
}

function formatSignedConstant(value) {
  if (value === 0) return "";
  return `${value > 0 ? "+" : "-"} ${formatMixedFractionLatex(Math.abs(value))}`;
}

function formatBinomial(variable, constant) {
  if (Math.abs(constant) < EPSILON) return variable;
  const sign = constant > 0 ? "+" : "-";
  return `(${variable} ${sign} ${formatMixedFractionLatex(Math.abs(constant))})`;
}

// value is the coordinate of the center, hence the opposite sign in (x - value).
export function formatShiftedLatex(variable, value) {
  return formatBinomial(variable, -value);
}

function canonicalValues(data) {
  const centerX = -data.Dx / 2;
  const centerY = -data.Ey / 2;
  const halfDx = data.Dx / 2;
  const halfEy = data.Ey / 2;
  const rSquared = halfDx ** 2 + halfEy ** 2 - data.F;
  return { centerX, centerY, halfDx, halfEy, rSquared };
}

function canonicalAnswer(data) {
  const { centerX, centerY, rSquared } = canonicalValues(data);
  return `${formatShiftedLatex("x", centerX)}^2 + ${formatShiftedLatex("y", centerY)}^2 = ${formatMixedFractionLatex(rSquared)}`;
}

function generalEquationLatex({ Dx, Ey, F }) {
  return `x^2 + y^2 ${formatSignedTerm("x", Dx)} ${formatSignedTerm("y", Ey)} ${formatSignedConstant(F)} = 0`
    .replace(/\s+/g, " ")
    .trim();
}

export function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function getRandomNonZeroEven(min, max) {
  let value = 0;
  while (value === 0 || value % 2 !== 0) value = getRandomInt(min, max);
  return value;
}

export function buildTask({ Dx, Ey, F }) {
  const values = canonicalValues({ Dx, Ey, F });
  if (values.rSquared <= 0) {
    throw new Error("Równanie nie opisuje okręgu o dodatnim promieniu.");
  }

  return {
    type: "ConvertToCanonicalTask",
    data: {
      Dx,
      Ey,
      F,
      a: values.centerX,
      b: values.centerY,
      rSquared: values.rSquared,
      center: { x: values.centerX, y: values.centerY },
    },
    answer: canonicalAnswer({ Dx, Ey, F }),
  };
}

export function generateTask() {
  const Dx = getRandomNonZeroEven(-8, 8);
  const Ey = getRandomNonZeroEven(-8, 8);

  let F;
  do {
    F = getRandomInt(-10, 10);
  } while ((Dx / 2) ** 2 + (Ey / 2) ** 2 - F <= 0);

  return buildTask({ Dx, Ey, F });
}

export function renderTask(task) {
  return (
    <div className="space-y-2">
      <p>Przekształć poniższe równanie okręgu do postaci kanonicznej:</p>
      <BlockMath math={generalEquationLatex(task.data)} />
    </div>
  );
}

export function renderInput(task, userInput, setUserInput, disabled) {
  return (
    <EquationInput
      value={userInput || ""}
      onChange={(value) => setUserInput(value)}
      disabled={disabled}
      placeholder="(x - a)^2 + (y - b)^2 = r^2"
    />
  );
}

export function parseCanonicalEquation(rawInput) {
  return parseCanonicalCircleEquation(rawInput);
}

export function checkAnswer(task, userInput) {
  return checkCircleEquation(userInput, canonicalValues(task.data), { canonical: true });
}

export function renderAnswer(task) {
  return (
    <div style={{ textAlign: "left" }}>
      <div style={{ display: "inline-block" }}>
        <BlockMath math={canonicalAnswer(task.data)} />
      </div>
    </div>
  );
}

export function renderExplanation(input) {
  const data = input?.data || input;
  const { Dx, Ey, F } = data;
  const { centerX, centerY, halfDx, halfEy, rSquared } = canonicalValues(data);
  const movedConstant = -F;
  const squareX = halfDx ** 2;
  const squareY = halfEy ** 2;
  const xGroup = `x^2 ${formatSignedTerm("x", Dx)}`.trim();
  const yGroup = `y^2 ${formatSignedTerm("y", Ey)}`.trim();

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Przenosimy wyraz wolny na prawą stronę i grupujemy wyrazy z <InlineMath math="x" /> oraz <InlineMath math="y" />.
        <BlockMath math={`${xGroup} + ${yGroup} = ${formatMixedFractionLatex(movedConstant)}`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Dopełniamy oba wyrażenia do kwadratów. Dodajemy kwadraty połowy współczynników przy <InlineMath math="x" /> i <InlineMath math="y" /> po obu stronach równania.
        <BlockMath
          math={`\\left(${xGroup} + ${formatMixedFractionLatex(squareX)}\\right) + \\left(${yGroup} + ${formatMixedFractionLatex(squareY)}\\right) = ${formatMixedFractionLatex(movedConstant)} + ${formatMixedFractionLatex(squareX)} + ${formatMixedFractionLatex(squareY)}`}
        />
        <BlockMath math={`${formatBinomial("x", halfDx)}^2 + ${formatBinomial("y", halfEy)}^2 = ${formatMixedFractionLatex(rSquared)}`} />
      </div>

      <div>
        <strong>Krok 3:</strong> Otrzymaliśmy postać kanoniczną okręgu.
        <BlockMath math={canonicalAnswer(data)} />
        <p>Jego środkiem jest <InlineMath math={`S = (${formatMixedFractionLatex(centerX)},\\ ${formatMixedFractionLatex(centerY)})`} />.</p>
      </div>
    </div>
  );
}

function toLatexFraction(value) {
  return String(value)
    .replace(/(-?\d+)\s+(\d+)\/(\d+)/g, "$1\\dfrac{$2}{$3}")
    .replace(/(?<!\\dfrac\{)(-?\d+)\/(\d+)/g, "\\dfrac{$1}{$2}");
}

export function renderUserAnswer(userInput) {
  if (!userInput || typeof userInput !== "string" || userInput.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ textAlign: "left" }}>
      <div style={{ display: "inline-block" }}>
        <BlockMath math={toLatexFraction(userInput)} />
      </div>
    </div>
  );
}

export function generateTasks() {
  return Array.from({ length: 3 }, () => generateTask());
}
